import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authAPI, debugCookies, testCookieSetting } from '../services/api';
import { Mail, Lock, LogIn, Eye, EyeOff, Bug } from 'lucide-react';
import ParticlesBackground from '../components/ParticlesBackground';
import {
  LoginContainer,
  BackgroundOverlay,
  AnimatedShape,
  ContentWrapper,
  LoginCard,
  LogoSection,
  Title,
  Subtitle,
  Form,
  InputGroup,
  InputIcon,
  Input,
  PasswordToggle,
  ErrorMessage,
  SubmitButton,
  LoadingSpinner,
  Footer
} from './Login.styles';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { login, error, isAuthenticated, clearError } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Handle URL parameters for session expired messages
  useEffect(() => {
    // Check for session expired parameter
    const sessionExpired = searchParams.get('error');
    if (sessionExpired === 'session_expired') {
      // Clear any existing error and set session expired message
      clearError();
      // The error will be set by the AuthContext
    }
  }, [searchParams, clearError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;

    setIsLoading(true);
    const result = await login(email, password);

    console.log('Login result:', result);
    console.log('User role:', result.user?.role);

    if (result.success) {
      // Redirect based on user role
      const userRole = result.user?.role;
      
      console.log('Redirecting based on role:', userRole);
      
      if (userRole === 'super_admin') {
        // Super admins go to the approval page
        console.log('Navigating to /approval');
        navigate('/approval');
      } else {
        // Regular admins go to dashboard
        console.log('Navigating to /dashboard');
        navigate('/dashboard');
      }
    } else {
      console.log('Login failed:', result.message);
    }
    setIsLoading(false);
  };

  // Debug function to test verify endpoint manually
  const testVerifyEndpoint = async () => {
    console.log('🧪 Testing verify endpoint manually...');
    debugCookies();
    try {
      const response = await authAPI.verify();
      console.log('✅ Verify endpoint success:', response.data);
    } catch (error) {
      console.log('❌ Verify endpoint error:', error.response?.data);
    }
  };

  return (
    <LoginContainer>
      <ParticlesBackground />
      
      <BackgroundOverlay>
        <AnimatedShape delay="0s" />
        <AnimatedShape delay="5s" />
        <AnimatedShape delay="10s" />
      </BackgroundOverlay>

      <ContentWrapper>
        <LoginCard>
          <LogoSection>
            <Title>CMS</Title>
            <Subtitle>Content Management System</Subtitle>
          </LogoSection>

          <Form onSubmit={handleSubmit}>
            <InputGroup>
              <InputIcon>
                <Mail size={20} />
              </InputIcon>
              <Input
                type="email"
                placeholder="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </InputGroup>

            <InputGroup>
              <InputIcon>
                <Lock size={20} />
              </InputIcon>
              <Input
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <PasswordToggle
                type="button"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </PasswordToggle>
            </InputGroup>

            {error && (
              <ErrorMessage>
                {error}
              </ErrorMessage>
            )}

            <SubmitButton type="submit" disabled={isLoading}>
              {isLoading ? (
                <LoadingSpinner />
              ) : (
                <>
                  <LogIn size={20} />
                  Sign In
                </>
              )}
            </SubmitButton>
          </Form>

          <Footer>
            Secure login for administrators
            <br />
            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
              <button 
                onClick={testVerifyEndpoint}
                style={{
                  padding: '5px 10px',
                  background: '#f0f0f0',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px'
                }}
              >
                <Bug size={12} />
                Test Verify
              </button>
              <button 
                onClick={testCookieSetting}
                style={{
                  padding: '5px 10px',
                  background: '#e0f0ff',
                  border: '1px solid #007acc',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px'
                }}
              >
                <Bug size={12} />
                Test Cookies
              </button>
            </div>
          </Footer>
        </LoginCard>
      </ContentWrapper>
    </LoginContainer>
  );
};

export default Login;
